import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import formatDateLLL from '@/util/formatDateLLL';
import LoadingMask from '../shared/LoadingMask';
import {
  Container,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import statuses from './statuses';

import { fetchPayments, clearPayments } from '../../actions/payments';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const PaymentHistory = () => {
  const dispatch = useDispatch();
  const payments = useSelector((state) => state.payments);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadPayments = async () => {
      await dispatch(fetchPayments());
      setLoaded(true);
    };
    loadPayments();
    return () => {
      dispatch(clearPayments());
    };
  }, [dispatch]);

  const columns = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      valueGetter: (value, row) => formatDateLLL(row.timestamp),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      valueGetter: (value, row) => formatter.format(row.amount),
    },
    {
      field: 'credits',
      headerName: 'Credits',
      flex: 1,
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment Method',
      flex: 1,
      valueGetter: (value, row) => `Card x${row.last4}`,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      valueGetter: (value, row) => (row.automated ? 'Automated' : 'Manual'),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      valueGetter: (value, row) =>
        row.status
          .toLowerCase()
          .replace(/\b(\w)/g, (s) => s.toUpperCase()),
    },
  ];

  const rows = payments
    .filter((payment) => payment.status !== statuses.CANCELED)
    .map((payment) => ({
      id: payment.payment_uuid,
      ...payment,
    }));

  if (!loaded) {
    return <LoadingMask />;
  }

  return (
    <Container>
          <Paper sx={{ p: 3 }}>
            <Typography variant='h4' align='center' gutterBottom>
              Payment History
            </Typography>

            <Box sx={{ width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={25}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                autoHeight
                sx={{
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                }}
              />
            </Box>
          </Paper>
        </Container>
  );
};

export default PaymentHistory;
